import React, { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import CustomTable from "../../components/universal/CustomTable";
import ConfirmToast from "../../components/universal/ConfirmToast";
import PlanModal from "./PlanModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AllPlansHooks, handleDeletePlan } from "./hooks/PlanHooks";
import { readTWGPlansRequest, savePlanLinkRequest } from "../../store/Plan/planActions";
import ReactModal from "react-modal";
import { IoCloseOutline } from "react-icons/io5";
import planPlaceholderImage from "../../assets/images/bibleplan.jpg";

const allPlansHeader = [
  { headerName: "Plan Name", headerId: "planName", width: "w-48" },
  { headerName: "Description", headerId: "planDescription", width: "w-64" },

  { headerName: "Actions", headerId: "actions", width: "w-32" },
];

//function to validate image url before rendering
const isRenderableImageUrl = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

//function to resolve TWG plan thumbnail using the shared thumbnail contract
const getPlanThumbnailUrl = (plan = {}) => {
  const gallery = plan?.gallery || {};
  const candidates = [
    gallery?.imageUrl,
    gallery?.mobileImageUrl,
    gallery?.tabImageUrl,
    gallery?.galleryUrl,
    gallery?.url_link,
    gallery?.url,
    gallery?.uri,
    gallery?.path,
    plan?.plansCoverImageUrl,
    plan?.planThumbnailUrl,
    plan?.thumbnailUrl,
  ];

  return candidates.find(isRenderableImageUrl) || "";
};

function AllPlans({ church, typeofentity }) {
  const { reloadData, setReloadData, fetchAllPlans, dispatch } = AllPlansHooks();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // New states for TWG Plan Selection
  const [isTWGModalOpen, setIsTWGModalOpen] = useState(false);
  const [twgPlans, setTwgPlans] = useState([]);
  const [selectedTWGPlans, setSelectedTWGPlans] = useState([]);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [isFetchingTWG, setIsFetchingTWG] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleSelectTWGPlanClick = () => {
    setIsFetchingTWG(true);
    setIsTWGModalOpen(true);
    dispatch(readTWGPlansRequest({ typeonplan: 1 }, (res) => {
      setIsFetchingTWG(false);
      if (res.type === 1) {
        setTwgPlans(res.data || []);
      } else {
        toast.error(res.error || "Failed to fetch TWG plans");
      }
    }));
  };

  const togglePlanSelection = (planId) => {
    setSelectedTWGPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
  };

  const handleCloseTWGModal = () => {
    setIsTWGModalOpen(false);
    if (selectedTWGPlans.length > 0) {
      setShowSaveButton(true);
    }
  };

  const handleSaveSelectedPlans = async () => {
    if (selectedTWGPlans.length === 0) return;

    setIsSavingLinks(true);
    let successCount = 0;
    let failCount = 0;

    for (const planId of selectedTWGPlans) {
      const payload = {
        _id: church?._id,
        planObjId: planId
      };

      await new Promise((resolve) => {
        dispatch(savePlanLinkRequest(payload, (res) => {
          if (res.type === 1) {
            successCount++;
          } else {
            failCount++;
          }
          resolve();
        }));
      });
    }

    setIsSavingLinks(false);
    if (successCount > 0) {
      toast.success(`${successCount} plan(s) linked successfully!`);
      setSelectedTWGPlans([]);
      setShowSaveButton(false);
      setReloadData(prev => !prev);
    }
    if (failCount > 0) {
      toast.error(`Failed to link ${failCount} plan(s).`);
    }
  };

  const transformTableData = (data) => {
    return data.map(plan => ({
      ...plan,
      planName: plan.planName || "Untitled Plan",
      planDescription: plan.planDescription || plan.description || "No description",
      noOfDays: plan.noOfDays || plan.noofDays || "0",
      createdDate: formatDate(plan.createdDate),
      // Add these fields for the table row
      churchId: plan.churchId || plan.churchObjId || "",
      planObjId: plan.planObjId || plan._id || "",
      galleryObjId: plan.galleryObjId,
      gallery: plan.gallery,
      dayPlans: plan.dayPlans || plan.dayplans || []
    }));
  };

  return (
    <div className="App bg-[#191919] min-h-screen">
      <div className="relative p-4">
        <div className="flex justify-end items-center mb-6">

          <div className="flex gap-4">
            {showSaveButton && (
              <button
                onClick={handleSaveSelectedPlans}
                disabled={isSavingLinks}
                className="flex items-center gap-2 text-[13px] text-yellowish border px-6 py-2 border-yellowish bg-blackish rounded-xl hover:bg-yellowish hover:text-black transition-colors disabled:opacity-50"
              >
                {isSavingLinks ? "Saving..." : "Save Selected Plan"}
              </button>
            )}
            <button
              onClick={handleSelectTWGPlanClick}
              className="flex items-center gap-2 text-[13px] text-yellowish border px-6 py-2 border-yellowish bg-blackish rounded-xl hover:bg-yellowish hover:text-black transition-colors"
            >
              Select TWG Plan
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 text-[13px] text-yellowish border px-6 py-2 border-yellowish bg-blackish rounded-xl hover:bg-yellowish hover:text-black transition-colors"
            >
              <AiOutlinePlus /> Add New Plan
            </button>
          </div>
        </div>

        {!isModalOpen && (
          <CustomTable
            searchFor="Plans"
            heightDesktop="h-[68vh]"
            width="w-full"
            nthChildWidth="[&>*:nth-child(1)]:w-48 [&>*:nth-child(2)]:w-64 [&>*:nth-child(3)]:w-32 [&>*:nth-child(4)]:w-40 [&>*:nth-child(5)]:w-32"
            notificationTable={true}
            headers={allPlansHeader}
            data={[]} // dynamic via callback
            customDataFetch={true}
            customDataFetchFunction={(page, limit, searchCriteria, callback) =>
              fetchAllPlans(page, limit, searchCriteria, callback, church?._id, 1, typeofentity)
            }
            dataTransformer={transformTableData}
            reloadData={reloadData}
            setReloadData={setReloadData}
            reverseTable={false}
            handleDeleteClick={(row) => {
              confirmAlert({
                customUI: ({ onClose }) => (
                  <ConfirmToast
                    title="Delete Plan"
                    message={`Are you sure you want to delete "${row.planName}"? This action cannot be undone.`}
                    onClose={onClose}
                    onSuccess={() => {
                      handleDeletePlan(
                        dispatch,
                        row.churchId,
                        row.planObjId,
                        (res) => {
                          if (res.type === 1) {
                            toast.success("Plan deleted successfully!", {
                              position: "top-right",
                              autoClose: 3000,
                            });
                            setReloadData((prev) => !prev);
                          } else {
                            toast.error(res.message || "Failed to delete plan!", {
                              position: "top-right",
                              autoClose: 3000,
                            });
                          }
                          onClose();
                        }
                      );
                    }}
                  />
                ),
              });
            }}
            handleEditClick={(row) => {
                            setSelectedRow(row);
              setIsModalOpen(true);
            }}
            showViewDetails={true}
            handleViewDetailsClick={(row) => {
              // You can implement a view details modal here
                            toast.info(`Viewing details for "${row.planName}"`, {
                position: "top-right",
                autoClose: 2000,
              });
            }}
            emptyStateMessage="No plans found. Click 'Add New Plan' to create your first plan."
            showSearch={true}
            searchPlaceholder="Search plans by name or description..."
          />
        )}
      </div>

      <PlanModal
        church={church}
        typeofentity={typeofentity}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRow(null);
          setReloadData((prev) => !prev); // Refresh table data
        }}
        rowData={selectedRow}
        onSuccess={() => {
          setReloadData((prev) => !prev); // Refresh table data on success
        }}
      />

      {/* TWG Plan Selection Modal */}
      <ReactModal
        isOpen={isTWGModalOpen}
        onRequestClose={handleCloseTWGModal}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#1f1f1f',
            border: 'none',
            borderRadius: '12px',
            width: '80%',
            maxHeight: '80vh',
            padding: '0'
          }
        }}
      >
        <div className="flex flex-col h-full text-white">
          <div className="flex justify-between items-center p-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold">Select TWG Plans</h2>
            <button onClick={handleCloseTWGModal} className="p-1 hover:bg-gray-700 rounded-full transition-colors">
              <IoCloseOutline className="text-2xl" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto">
            {isFetchingTWG ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellowish"></div>
              </div>
            ) : twgPlans.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                No TWG plans available.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {twgPlans.map((plan) => {
                  const thumbnailUrl = getPlanThumbnailUrl(plan);
                  const isSelected = selectedTWGPlans.includes(plan._id);

                  return (
                    <div
                      key={plan._id}
                      onClick={() => togglePlanSelection(plan._id)}
                      className={`relative cursor-pointer rounded-xl overflow-hidden border-2 bg-[#2a2a2a] transition-all transform hover:scale-[1.02] ${
                        isSelected
                          ? "border-yellowish ring-2 ring-yellowish/50"
                          : "border-transparent hover:border-gray-600"
                      }`}
                    >
                      <div className="relative h-36 w-full bg-blackish">
                        <img
                          src={thumbnailUrl || planPlaceholderImage}
                          alt={plan.planName || "TWG Plan"}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.src = planPlaceholderImage;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-yellowish mb-2">
                          {plan.noOfDays || plan.noofDays || plan.dayPlans?.length || plan.dayplans?.length || 0} Days
                        </p>
                        <h3 className="font-bold text-lg mb-1 truncate">{plan.planName || "Untitled Plan"}</h3>
                        <p className="text-gray-400 text-sm line-clamp-2">{plan.description || plan.planDescription || "No description provided."}</p>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-yellowish text-black rounded-full p-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-700 flex justify-end gap-4">
            <button
              onClick={handleCloseTWGModal}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleCloseTWGModal}
              disabled={selectedTWGPlans.length === 0}
              className="px-8 py-2 bg-yellowish hover:bg-yellow-500 text-black rounded-xl transition-colors font-bold disabled:opacity-50"
            >
              Done ({selectedTWGPlans.length})
            </button>
          </div>
        </div>
      </ReactModal>
    </div>
  );
}

export default AllPlans;
